/* eslint-disable max-lines-per-function */
import React, { useState } from "react";
import { Link } from "react-router-dom";

import { LazyQueryExecFunction, useLazyQuery } from "@apollo/client";

import * as yup from "yup";
import { Form, Formik } from "formik";
import {
  Grid,
  Button, Checkbox, CheckboxProps, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from "@mui/material";
import { Check as CheckIcon } from "@mui/icons-material";
import { InputWithErrorLabel } from "components/haven-design/form/inputs";
import { AdminListUsersQuery, AdminListUsersQueryVariables } from "types/gql-types";
import { CopyButton } from "components/haven-design/copy-button";

import { Modal } from "components/haven-design";
import { LIST_USERS_QUERY } from "./queries";
import MergeAccounts from "./merge-accounts";

import { usePersistedSearch } from "./use-persisted-search";

const schema = yup.object({
  name: yup.string(),
  loginEmail: yup.string(),
  loginPhone: yup.string(),
});

type Values = yup.InferType<typeof schema>
const initialValues: Values = {
  name: "",
  loginEmail: "",
  loginPhone: "",
};

const SearchForm: React.FC<{
  searchUsers: LazyQueryExecFunction<AdminListUsersQuery, AdminListUsersQueryVariables>,
  data: AdminListUsersQuery | undefined,
  loading: boolean,
  selected: string[],
}> = ({ searchUsers, data, loading, selected }) => {

  const [merging, setMerging] = useState(false);
  const closeMerging = () => setMerging(false);

  const [formValues, search] = usePersistedSearch(initialValues, (variables) => {
    searchUsers({ variables });
  });

  return (
    <>
      {data?.adminApplicants && (
        <Modal
          fullWidth
          title="Merge Accounts"
          isOpen={merging}
          onRequestClose={closeMerging}>
          <MergeAccounts
            users={data.adminApplicants.filter(({ id }) => selected.includes(id))}
            close={closeMerging} />
        </Modal>
      )}
      <Formik initialValues={formValues} validationSchema={schema} onSubmit={search}>
        <Form>
          <Grid container spacing={2}>
            <Grid>
              <InputWithErrorLabel name="name" type="text" label="Profile Name is like" />
            </Grid>

            <Grid>
              <InputWithErrorLabel name="loginEmail" type="text" label="Login Email is like" />
            </Grid>

            <Grid>
              <InputWithErrorLabel name="loginPhone" type="text" label="Login Phone is like" />
            </Grid>

            <Grid size={{ xs: 12 }} container spacing={2}>
              <Grid size={{ xs: 2 }}>
                <Button variant="contained" type="submit" color="primary" disabled={loading}>
                  Search
                </Button>
              </Grid>

              <Grid container size={{ xs: 4 }} alignContent="center">
                {data?.adminApplicants?.length || 0} Results, {selected.length} Selected
              </Grid>

              <Grid container size={{ xs: 6 }} justifyContent="flex-end" spacing={2}>
                <Grid size={{ xs: 3 }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => setMerging(true)}
                    disabled={selected.length < 2}
                  >
                  Merge {selected.length} Accounts
                  </Button>
                </Grid>

                <Grid size={{ xs: 3 }}>
                  <Button variant="contained" color="primary" href="/dash/admin/users/new">Create User</Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Form>
      </Formik>
    </>);
};

const ApplicantsIndex: React.FC = () => {
  const [searchUsers, { loading, data, error }] = useLazyQuery<
  AdminListUsersQuery,
  AdminListUsersQueryVariables
  >(LIST_USERS_QUERY, { fetchPolicy: "network-only" });

  const [selected, setSelected] = useState<string[]>([]);

  const addSelected = (id: string) => setSelected([...selected, id]);
  const removeSelected = (id: string) => setSelected(selected.filter(item => item !== id));
  const isSelected = (id: string) => selected.includes(id);

  const allSelected = selected.length === data?.adminApplicants?.length;

  const handleSelectAllChange: CheckboxProps["onChange"] = (_, checked) => {
    if (checked) {
      return setSelected(
        (data?.adminApplicants || []).map(({ id }) => id) as string[],
      );
    }
    return setSelected([]);
  };

  const handleSelectChange: (id: string) => CheckboxProps["onChange"] = (id) => (_e, checked) => {
    if (checked) return addSelected(id);
    return removeSelected(id);
  };

  return (
    <>
      <SearchForm searchUsers={searchUsers} data={data} loading={loading} selected={selected} />

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Checkbox onChange={handleSelectAllChange} checked={allSelected} />
              </TableCell>
              <TableCell>ID</TableCell>
              <TableCell>First Name</TableCell>
              <TableCell>Last Name</TableCell>
              <TableCell>Login Email</TableCell>
              <TableCell>Login Phone</TableCell>
              <TableCell># of Interests</TableCell>
              <TableCell>Archived?</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>

            {loading && (
              <TableRow>
                <TableCell align="center" colSpan={7}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            )}

            {error && (
              <TableRow>
                <TableCell align="center" colSpan={7}>
                  <strong>Error loading results</strong>
                </TableCell>
              </TableRow>
            )}

            {!loading && !error && (<>
              {!data?.adminApplicants && (
                <TableRow>
                  <TableCell align="center" colSpan={7}>
                    <strong>No Results</strong>
                  </TableCell>
                </TableRow>
              )}

              {data?.adminApplicants?.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Checkbox onChange={handleSelectChange(row.id)} checked={isSelected(row.id)} />
                  </TableCell>
                  <TableCell>
                    {row.id}
                    <CopyButton text={row.id} />
                  </TableCell>
                  <TableCell>{row.profile?.firstName}</TableCell>
                  <TableCell>{row.profile?.lastName}</TableCell>
                  <TableCell>{row.loginEmail}<CopyButton text={row.loginEmail} /></TableCell>
                  <TableCell>{row.loginPhone}</TableCell>
                  <TableCell>{row.interests?.length || 0}</TableCell>
                  <TableCell>{row.isArchived && <CheckIcon data-cy="archived" />}</TableCell>
                  <TableCell>
                    <Link to={`/dash/admin/users/${row.id}`}>Details</Link>
                  </TableCell>
                </TableRow>
              ))}
            </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

    </>
  );
};

export default ApplicantsIndex;
